import { isPaidPortalMember } from "./membershipTier";

/** Tokens stored in Event.freeForGroups (comma-separated). */
export const EVENT_FREE_FOR_GROUPS = [
  "Explorer",
  "EarlyStage",
  "Business",
  "Executive",
] as const;

export type EventFreeForGroup = (typeof EVENT_FREE_FOR_GROUPS)[number];

export const EVENT_FREE_FOR_GROUP_LABELS: Record<EventFreeForGroup, string> = {
  Explorer: "Explorers",
  EarlyStage: "Early Stage",
  Business: "Business",
  Executive: "Executive",
};

export function parseFreeForGroups(
  freeForGroups?: string | null,
): EventFreeForGroup[] {
  if (!freeForGroups?.trim()) return [];
  const allowed = new Set<string>(EVENT_FREE_FOR_GROUPS);
  return freeForGroups
    .split(",")
    .map((p) => p.trim())
    .filter((p): p is EventFreeForGroup => allowed.has(p));
}

export function serializeFreeForGroups(
  selected: readonly string[],
): string | null {
  const set = new Set(selected.map((s) => s.trim()));
  const ordered = EVENT_FREE_FOR_GROUPS.filter((g) => set.has(g));
  return ordered.length === 0 ? null : ordered.join(",");
}

export function isEventFreeForGroup(
  freeForGroups: string | null | undefined,
  group: EventFreeForGroup,
): boolean {
  return parseFreeForGroups(freeForGroups).includes(group);
}

/** Map portal user to free-for group token (mirrors API EventFreeForGroups). */
export function resolveEventFreeForGroupForUser(user?: {
  membershipName?: string | null;
  hasPaid?: number | null;
  stripeStatus?: string | null;
} | null): EventFreeForGroup | null {
  if (!user) return null;
  if (!isPaidPortalMember(user)) return "Explorer";
  const name = (user.membershipName ?? "").trim().toLowerCase();
  if (name === "early stage membership" || name === "youth membership") {
    return "EarlyStage";
  }
  if (name === "business membership") return "Business";
  if (name === "executive membership") return "Executive";
  return null;
}

export function isEventAdminFreeForUser(
  freeForGroups: string | null | undefined,
  user?: {
    membershipName?: string | null;
    hasPaid?: number | null;
    stripeStatus?: string | null;
  } | null,
): boolean {
  const group = resolveEventFreeForGroupForUser(user);
  if (!group) return false;
  return isEventFreeForGroup(freeForGroups, group);
}
